// Partes menores da tela de marca d'agua: galeria dos modelos, logo e
// onde aplicar.

import { useRef } from "react";
import { Check, ImagePlus, Loader2, Trash2 } from "lucide-react";
import {
  WATERMARK_FLOWS,
  WATERMARK_PRESETS,
  type WatermarkFlow,
  type WatermarkPresetId,
} from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Segmented } from "@/components/forwarding/controls";
import { FLOW_LABELS } from "@/lib/watermark";
import { cn } from "@/lib/utils";

/** Miniaturas geradas pelo renderizador real (scripts/render-watermark-presets). */
export function presetThumb(id: WatermarkPresetId): string {
  return `/watermark/presets/${id}.jpg`;
}

interface GalleryProps {
  readonly selected: WatermarkPresetId | null;
  readonly onPick: (id: WatermarkPresetId) => void;
  readonly disabled?: boolean;
}

function PresetCard({
  preset,
  active,
  disabled,
  onPick,
}: {
  preset: (typeof WATERMARK_PRESETS)[number];
  active: boolean;
  disabled: boolean | undefined;
  onPick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      disabled={disabled}
      onClick={onPick}
      className={cn(
        "group flex flex-col overflow-hidden rounded-lg border text-left transition-all duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed",
        active
          ? "border-primary ring-2 ring-primary/30"
          : "border-border hover:-translate-y-0.5 hover:border-primary/60",
      )}
    >
      <span className="relative block aspect-square bg-muted">
        <img
          src={presetThumb(preset.id)}
          alt=""
          loading="lazy"
          width={360}
          height={360}
          className="h-full w-full object-cover"
        />
        {active && (
          <span className="absolute right-1.5 top-1.5 rounded-full bg-primary p-0.5 text-primary-foreground">
            <Check size={12} />
          </span>
        )}
      </span>
      <span className="space-y-0.5 p-2">
        <span className="block text-sm font-medium">{preset.name}</span>
        <span className="block text-[11px] leading-snug text-muted-foreground">
          {preset.description}
        </span>
      </span>
    </button>
  );
}

export function PresetGallery({ selected, onPick, disabled }: GalleryProps): JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label="Modelos de marca d'água"
      className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
    >
      {WATERMARK_PRESETS.map((preset) => (
        <PresetCard
          key={preset.id}
          preset={preset}
          active={preset.id === selected}
          disabled={disabled}
          onPick={() => onPick(preset.id)}
        />
      ))}
    </div>
  );
}

interface LogoCardProps {
  readonly logoUrl: string | null;
  readonly busy: boolean;
  readonly onUpload: (file: File) => void;
  readonly onRemove: () => void;
}

export function LogoCard({ logoUrl, busy, onUpload, onRemove }: LogoCardProps): JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-center gap-4">
      <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-[repeating-conic-gradient(hsl(var(--muted))_0%_25%,transparent_0%_50%)] bg-[length:12px_12px]">
        {logoUrl ? (
          <img src={logoUrl} alt="Logo atual" className="max-h-full max-w-full object-contain" />
        ) : (
          <ImagePlus size={18} className="text-muted-foreground" />
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-medium">Logo da marca</p>
        <p className="text-xs text-muted-foreground">
          PNG com fundo transparente fica melhor. PNG, JPEG ou WebP até 2 MB.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="sr-only"
            aria-label="Enviar logo"
            data-testid="logo-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUpload(file);
              e.target.value = "";
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => input.current?.click()}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
            {logoUrl ? "Trocar logo" : "Enviar logo"}
          </Button>
          {logoUrl && (
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onRemove}>
              <Trash2 size={13} /> Remover
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

interface FlowsCardProps {
  readonly applyTo: "all" | "selected";
  readonly flows: readonly WatermarkFlow[];
  readonly onApplyTo: (value: "all" | "selected") => void;
  readonly onFlows: (flows: WatermarkFlow[]) => void;
}

export function FlowsCard({ applyTo, flows, onApplyTo, onFlows }: FlowsCardProps): JSX.Element {
  return (
    <div className="space-y-3">
      <Segmented
        label="Onde aplicar"
        value={applyTo}
        onChange={onApplyTo}
        options={[
          { value: "all", label: "Em todas as imagens" },
          { value: "selected", label: "Escolher por fluxo" },
        ]}
      />
      {applyTo === "selected" && (
        <div className="grid gap-2 sm:grid-cols-2">
          {WATERMARK_FLOWS.map((flow) => {
            const checked = flows.includes(flow);
            return (
              <label
                key={flow}
                className={cn(
                  "flex cursor-pointer items-start gap-2 rounded-md border p-2.5",
                  checked ? "border-primary/50 bg-primary/5" : "border-border",
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    onFlows(checked ? flows.filter((f) => f !== flow) : [...flows, flow])
                  }
                  className="mt-0.5 accent-[hsl(var(--primary))]"
                />
                <span>
                  <span className="block text-sm font-medium">{FLOW_LABELS[flow].label}</span>
                  <span className="block text-[11px] text-muted-foreground">
                    {FLOW_LABELS[flow].hint}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
